import React, { useEffect, useMemo } from 'react';

import { Grid } from 'components/base';
import { formatDate, parseDate } from 'utils/format';
import chunk from 'utils/chunk';
import { getPageSize } from 'editor-utils/template';
import { formatData } from 'editor-utils/dataParser';

import useEditorContext from '../../context/editorContext';
import useEditorItemContext from '../../context/editorItemContext';
import pagesValidator from '../../lib/pagesValidator';

import PageWrapper from '../components/pageWrapper';
import Page from '../../components/page/preview';
import { Layout } from '../../components/rich';
import useEditorRouter from '../../lib/editorRouter';
import EditorLayout from '../components/layout';
import { Button } from '../../components/base';
import Icon, { ICON_TYPE } from '../../components/base/icon';
import OptionedContinueButton from '../components/optionedContinueButton';
import withPolotno from '../../hoc/withPolotno';
import useContentWizardContext from '../../contentWizard/contentWizardContext';

const CalendarPreview = props => {
  const editorContext = useEditorContext();
  const contentWizardContext = useContentWizardContext();

  const { item, product, template, config } = useEditorItemContext();
  const editorRouter = useEditorRouter(product.id);

  const [cover, pages, back] = useMemo(() => {
    const type = 'pages';
    const [width, height] = getPageSize(template);

    const pagesData = item.pages.map(page => ({
      ...page,
      size: { width, height },
      type,
      visible: true,
    }));

    const numberOfPages = pagesData.length;
    const { 0: firstPage, [numberOfPages - 1]: lastPage } = pagesData;
    // TODO there is no required cover or back
    return [firstPage, pagesData.slice(1, numberOfPages - 1), lastPage];
  }, [item.pages, template]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const events = item.additionalInfo?.events ?? [];
      if (events.length === 0) {
        contentWizardContext.openDialog({
          type: 'events',
        });
      }
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const handleAddEventsClick = () => {
    contentWizardContext.openDialog({
      type: 'events',
    });
  };

  const handleOnSuccessClick = async (checkErrors = true) => {
    const validator = pagesValidator(template, item.pages, config, 'calendar');
    validator.confirmation(
      'I have carefully reviewed my project for errors and agree to print it “as is”. I understand that once I submit my order it will be sent to the printer and cannot be canceled. Because this is a custom product, it is not returnable for credit or refund.',
    );

    const data = formatData(template, item, { validator });

    if (validator.isValid() || !checkErrors) {
      props.onSuccess({ data });
      return { data };
    }

    const errors = validator.getErrors();
    props.onSuccess({ errors });
    return { errors };
  };

  const handleOnPageAction = (pageKey, action) => {
    switch (action) {
      case 'edit':
        props.startPolotno(
          {
            pageKey,
          },
          result => {
            editorContext.changePage(item.id, result.page, { pageKey: result.pageKey });
          },
        );
        break;

      default:
        console.log({ pageKey, action });
        break;
    }
  };

  const handleOnPageClick = (e, pageKey) => {
    e.preventDefault();

    handleOnPageAction(pageKey, 'edit');
  };

  return (
    <EditorLayout
      actionBar={
        <>
          {props.actionBar}
          {config.contentOptions != null && (
            <Button variant="secondary" onClick={handleAddEventsClick} mr={[1, 1, 2]}>
              <Icon type={ICON_TYPE.ADD} width={8} height={8} />
              Add Events
            </Button>
          )}
        </>
      }
      actionButton={
        <OptionedContinueButton onClick={handleOnSuccessClick}>Add to Cart</OptionedContinueButton>
      }
    >
      <Layout.Container mt={3} mb={3} pl={0} pr={0}>
        <div className="text-center mx-auto">
          <Grid.Row>
            <Grid.Col xs={6} sm={8} />
            {cover && (
              <Grid.Col xs={6} sm={4} className="col-page-preview mb-5">
                <PageWrapper
                  id={`page-${cover.index}`}
                  label="Cover Page"
                  buttonPosition="center"
                  editable={false}
                >
                  <Page
                    page={cover}
                    photos={item.photos}
                    template={template}
                    fullWidth
                    serverSideDPI={72}
                    onClick={e => handleOnPageClick(e, cover.key)}
                  />
                </PageWrapper>
              </Grid.Col>
            )}
          </Grid.Row>

          <Grid.Row>
            {chunk(pages, 2).map((pagesChunk, index) => {
              const monthPage = pagesChunk[pagesChunk.length - 1];

              const date = parseDate(item.additionalInfo.initialMonth, 'YYYY-MM');
              date.setMonth(date.getMonth() + index);

              return (
                <React.Fragment key={monthPage.key}>
                  <Grid.Col xs={6} sm={4} className="col-page-preview mb-5">
                    <PageWrapper
                      id={`page-${monthPage.index}`}
                      label={formatDate(date, 'MMMM YYYY')}
                      buttonPosition="center"
                      editable={false}
                    >
                      {pagesChunk.map(page => (
                        <Page
                          key={page.key}
                          page={page}
                          photos={item.photos}
                          template={template}
                          fullWidth
                          serverSideDPI={72}
                          onClick={e => handleOnPageClick(e, page.key)}
                        />
                      ))}
                    </PageWrapper>
                  </Grid.Col>
                </React.Fragment>
              );
            })}
          </Grid.Row>

          <Grid.Row>
            {back && (
              <Grid.Col xs={6} sm={4} className="col-page-preview mb-5">
                <PageWrapper
                  id={`page-${back.index}`}
                  buttonPosition="center"
                  label="Back Page"
                  editable={false}
                >
                  <Page
                    page={back}
                    photos={item.photos}
                    template={template}
                    fullWidth
                    serverSideDPI={72}
                    onClick={e => handleOnPageClick(e, back.key)}
                  />
                </PageWrapper>
              </Grid.Col>
            )}
          </Grid.Row>
        </div>
      </Layout.Container>
    </EditorLayout>
  );
};

export default withPolotno(CalendarPreview);
